import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipboardList, CheckCircle, XCircle, Clock } from 'lucide-react';
import BillingDetailSheet from '../../components/BillingDetailSheet';
import { fmtETDate } from '../../utils/tz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Estimates = () => {
  const [estimates, setEstimates] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => { fetchEstimates(); }, []);

  const fetchEstimates = async () => {
    try {
      const { data } = await axios.get(`${API}/estimates`, { withCredentials: true });
      setEstimates(data);
    } catch (error) { console.error('Error fetching estimates:', error); }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <Clock className="w-5 h-5 text-[#D4AF37]" />;
    }
  };

  const getStatusColor = (status) => ({
    approved: 'text-green-500 bg-green-500/10',
    rejected: 'text-red-500 bg-red-500/10',
    pending: 'text-[#D4AF37] bg-[#D4AF37]/10',
  })[status] || 'text-[#A3A3A3] bg-[#262626]';

  return (
    <div data-testid="estimates-page" className="space-y-8">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Estimates</h2>
        <p className="text-[#A3A3A3]">Review and approve your estimates</p>
      </div>

      <div className="grid gap-6">
        {estimates.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">No estimates found</div>
        ) : estimates.map((estimate) => (
          <button
            key={estimate.id}
            data-testid={`customer-estimate-${estimate.id}`}
            onClick={() => setSelected(estimate)}
            className="w-full text-left bg-[#141414] border border-[#262626] rounded-md p-6 hover:border-[#D4AF37] transition-all duration-200"
          >
            <div className="flex items-start justify-between">
              <div className="flex gap-4 flex-1">
                <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-md flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-medium text-lg mb-1">{estimate.estimate_number}</h3>
                  <p className="text-[#A3A3A3] text-sm mb-2">{estimate.notes || estimate.description || estimate.line_items?.[0]?.description || ''}</p>
                  <p className="text-[#A3A3A3] text-xs">{fmtETDate(estimate.date)}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[#D4AF37] text-2xl font-bold mb-2">${estimate.amount.toFixed(2)}</p>
                <div className={`flex items-center gap-2 px-3 py-1 rounded-md text-xs font-medium ${getStatusColor(estimate.status)}`}>
                  {getStatusIcon(estimate.status)}
                  {estimate.status.toUpperCase()}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <BillingDetailSheet
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        doc={selected}
        type="estimate"
        viewerRole="customer"
        onChange={fetchEstimates}
      />
    </div>
  );
};

export default Estimates;
